import { StreamStep } from 'src/v3/core/types'
import { PipelineShowNotes, PipelineVideo } from './course-api'

export type CourseContentItem =
  | { kind: 'showNotes'; data: PipelineShowNotes }
  | { kind: 'video'; data: PipelineVideo }

type OrderedCourseContent = {
  items: CourseContentItem[]
  hasUnsteppedItems: boolean
  hasStaleSteps: boolean
}

// Orders a course's show notes and videos by their stream step index. Items
// with no step yet (newly added before the first save) are appended at the
// end, show notes before videos.
export let orderCourseContent = (
  courseSteps: StreamStep[],
  showNotes: PipelineShowNotes[],
  videos: PipelineVideo[],
): OrderedCourseContent => {
  let snMap = new Map(showNotes.map((sn) => [sn.id, sn]))
  let vMap = new Map(videos.map((v) => [v.id, v]))
  let ordered: CourseContentItem[] = courseSteps
    .toSorted((a, b) => a.stepIndex - b.stepIndex)
    .flatMap((step): CourseContentItem[] => {
      if (step.showNotesID) {
        let sn = snMap.get(step.showNotesID)
        return sn ? [{ kind: 'showNotes', data: sn }] : []
      }
      if (step.videoID) {
        let v = vMap.get(step.videoID)
        return v ? [{ kind: 'video', data: v }] : []
      }
      return []
    })
  let steppedSnIDs = new Set(
    courseSteps.map((s) => s.showNotesID).filter(Boolean),
  )
  let steppedVIDs = new Set(courseSteps.map((s) => s.videoID).filter(Boolean))
  let unsteppedSn = showNotes
    .filter((sn) => !steppedSnIDs.has(sn.id))
    .map((sn): CourseContentItem => ({ kind: 'showNotes', data: sn }))
  let unsteppedV = videos
    .filter((v) => !steppedVIDs.has(v.id))
    .map((v): CourseContentItem => ({ kind: 'video', data: v }))
  let hasStaleSteps = courseSteps.some(
    (s) =>
      (s.showNotesID && !snMap.has(s.showNotesID)) ||
      (s.videoID && !vMap.has(s.videoID)),
  )

  return {
    items: [...ordered, ...unsteppedSn, ...unsteppedV],
    hasUnsteppedItems: unsteppedSn.length > 0 || unsteppedV.length > 0,
    hasStaleSteps: Boolean(hasStaleSteps),
  }
}
